import { Button } from "components/ui/button";
import { PostHogProvider } from "@posthog/react";
import * as Sentry from "@sentry/react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ApplicationLoading } from "components/loading";
import { SignedIn } from "components/signed-in";
import { ThemeKeybind } from "components/theme-keybind";
import { ThemeProvider } from "components/theme-provider";
import { SignedOut } from "components/signed-out";
import { CloseButton } from "components/snackbars/CloseButton";
import DeploymentStatusWidget from "components/deployment-status/DeploymentStatusWidget";
import { NoAccessToOrganisations } from "components/user/sign-in/no-access-to-organisations";
import { OnboardingCompleteSwitch } from "components/user/sign-in/onboarding-complete-switch";
import OnboardingWizard from "components/user/sign-in/onboarding-wizard";
import { useRegisterUTMs } from "components/user/useRegisterUTMs";
import { IdentityProvider, useIdentityContext } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchLatestVersion } from "data/queries-common";
import { SnackbarProvider } from "notistack";
import posthog from "posthog-js";
import React, { useState } from "react";
import { CookiesProvider } from "react-cookie";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { BrowserRouter as Router } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import {
  IntercomUnreadProvider,
  useSetIntercomUnread,
} from "contexts/intercom-unread-context";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import.meta.env.VITE_SENTRY_DSN &&
  import.meta.env.VITE_ENVIRONMENT &&
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
  });


const intercomAppId: string = import.meta.env.VITE_INTERCOM_APP_ID
  ? import.meta.env.VITE_INTERCOM_APP_ID
  : "";

const queryClient = new QueryClient();

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  person_profiles: "identified_only",
});

// configure stripe settings ...
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLIC_KEY
    ? import.meta.env.VITE_STRIPE_PUBLIC_KEY
    : ``,
);

const currentVersion = `v2.0.0.${import.meta.env.VITE_BUILD_NO}`;

const UserStateSwitch = () => {
  /**
   * Conditionally returns components depending if we're signed in or not
   */

  const { phase } = useIdentityContext();
  const [showUpdate, setShowUpdate] = useState(false);
  const [countdown, setCountdown] = useState(15);

  // Register UTM parameters from URL across all routes
  useRegisterUTMs();

  /**
   *
   * @param str check if the version string matches our conditions
   * @returns
   */
  const isValidVersionString = (str: string) => {
    if (
      str === undefined ||
      str === null ||
      str.trim() === "" ||
      str.includes("undefined")
    ) {
      return false;
    }
    // Regex pattern for `v2.0.0.1234` format
    const versionPattern = /^v\d+\.\d+\.\d+\.\d+$/;
    if (!versionPattern.test(str)) {
      return false; // The string does not matches the version format
    }
    return true; // The string is valid
  };

  const latestVersionQuery = useQuery({
    queryKey: [QueryKeys.latestVersion],
    queryFn: () => fetchLatestVersion(),
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  // Handle version check with useEffect (replaces onSuccess from v4)
  React.useEffect(() => {
    const latestVersion = latestVersionQuery.data;
    if (latestVersion) {
      if (latestVersion == "local") {
        return;
      }
      // check if the version strings are unsafe to do a force reload
      if (!isValidVersionString(latestVersion)) {
        return;
      }
      if (!isValidVersionString(currentVersion)) {
        return;
      }
      if (latestVersion !== currentVersion) {
        setShowUpdate(true);
        // Start countdown
        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev === 1) {
              clearInterval(interval);
              window.location.reload();
            }
            return prev - 1;
          });
        }, 1000);
      }
    }
  }, [latestVersionQuery.data]);

  const switcher = (() => {
    switch (phase) {
      case "loading":
        return <ApplicationLoading />;
      case "unauthenticated":
        return <SignedOut />;
      case "onboarding":
        return <OnboardingWizard />;
      case "pending-invite":
        return <OnboardingCompleteSwitch />;
      case "no-access":
        return <NoAccessToOrganisations />;
      case "app":
        return <SignedIn />;
    }
  })();

  return (
    <>
      {showUpdate && (
        <div
          role="status"
          className="bg-card text-card-foreground ring-foreground/10 fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-lg px-4 py-3 shadow-md ring-1"
        >
          <span className="text-sm">
            A new version is available. Reloading in {countdown}s...
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh Now
          </Button>
        </div>
      )}
      {switcher}
    </>
  );
};

const ReactQueryDevtoolsPreProd = React.lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    }),
  ),
);

// Bridges Intercom's `onUnreadCountChange` callback into IntercomUnreadContext
// so the sidebar can render an unread badge. Must render inside
// IntercomUnreadProvider to read the setter.
function IntercomWithUnread({ children }: { children: React.ReactNode }) {
  const setUnread = useSetIntercomUnread();

  return (
    <IntercomProvider appId={intercomAppId} onUnreadCountChange={setUnread}>
      {children}
    </IntercomProvider>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="geoscape-theme">
      <div className="App">
        <ThemeKeybind />
        <PostHogProvider client={posthog}>
          <QueryClientProvider client={queryClient}>
            <IntercomUnreadProvider>
              <IntercomWithUnread>
                <SnackbarProvider
                  maxSnack={3}
                  autoHideDuration={6000}
                  action={(snackbarId) => (
                    <CloseButton snackbarId={snackbarId} />
                  )}
                >
                  <Router>
                    <Elements stripe={stripePromise}>
                      <CookiesProvider>
                        <IdentityProvider>
                          <UserStateSwitch />
                        </IdentityProvider>
                      </CookiesProvider>
                    </Elements>
                    <DeploymentStatusWidget />
                  </Router>
                </SnackbarProvider>
              </IntercomWithUnread>
            </IntercomUnreadProvider>
            {import.meta.env.VITE_ENVIRONMENT !== "production" && (
              <React.Suspense fallback={null}>
                <ReactQueryDevtoolsPreProd initialIsOpen={false} />
              </React.Suspense>
            )}
          </QueryClientProvider>
        </PostHogProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
